import { useAuth0 } from '@auth0/auth0-react'
import { Container } from '@mui/material'
import React, { useEffect, useMemo, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { errorLog, infoLog } from '../../../utils/log-helper'
import RestaurantCardContainer from '../../../components/restaurant/RestaurantCardContainer'
import { generateEater } from '../../../helpers/converters'
import {
  getLocation,
  getGoogleMapsLocation,
  defaultLocationCoords,
} from '../../../helpers/location-helper'
import { ROUTES } from '../../../helpers/routes-helper'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { useDevice } from '../../../hooks/useDevice'
import { useAppSelector } from '../../../store'
import { selectAccessToken, selectLocation, setLocation } from '../../../store/authSlice'
import * as Sentry from "@sentry/react";
import {
  getLandingListWithPredictions,
  getLandingPageDetails,
  selectIsLoading,
  selectLandingDetail,
  selectLocalList,
  selectMessage,
  selectSeverity,
  selectShouldOpen,
  selectWalletList,
  setOpen,
} from '../../../store/landingSlice'
import { selectEater } from '../../../store/profileSlice'
import { selectRadius } from '../../../store/restaurantSlice'
import { ILandingDetailsRequest, LandingType } from '../../../types'
import useRudderStackAnalytics from '../../../useRudderAnalytics'
import { logEvent } from '../../../analytics/analytics'
import { MuiAlert, MuiSnackbar } from '../styled/global.styled'
import DownloadApp from './components/DownloadApp'
import RewardsCTABanner from './components/RewardsCTABanner'
import GroupDiningCTA from './components/GroupDiningCTA'
import GroupsConversationsSection from './components/GroupsConversationsSection'
import FoodAreasSection from './components/FoodAreasSection'
import MenuScanBanner from '../../../components/MenuScanBanner'
import ProfileCompletionBanner from '../../../components/ProfileCompletionBanner'
import { useMenuScanFlow } from '../../../hooks/useMenuScanFlow'
import { useLandingPoll } from '../../../hooks/useLandingPoll'
import { HomeRestaurantCardContainer } from './styled/Home.styled'
import { useLocation } from 'react-router'

const DocumentTitle =
  'Resactly | Discover Top Restaurants and Dining Experiences'

const Home: React.FC = () => {
  const { dispatch } = useAppUtilities()
  const { isAuthenticated } = useAuth0()
  const { startScan, uploading } = useMenuScanFlow()
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const user = useAppSelector(selectEater)
  const radius = useAppSelector(selectRadius)
  const message = useAppSelector(selectMessage)
  const location = useAppSelector(selectLocation)
  const severity = useAppSelector(selectSeverity)
  const loading = useAppSelector(selectIsLoading)
  const localList = useAppSelector(selectLocalList)
  const openServer = useAppSelector(selectShouldOpen)
  const walletList = useAppSelector(selectWalletList)
  const accessToken = useAppSelector(selectAccessToken)
  const landingDetail = useAppSelector(selectLandingDetail)
  const { isHybrid } = useDevice()
  const navLocation = useLocation()
  const hasEnrichedRef = useRef(false)
  const firstLandingRequestFiredRef = useRef(false)
  const firstLandingWasUnauthRef = useRef(false)
  const landingAuthRefreshDoneRef = useRef(false)
  const landingAuthRefreshInFlightRef = useRef(false)
  const homeLoadedLoggedRef = useRef(false)

  const devLandingLog = (label: string, data?: Record<string, unknown>): void => {
    if (process.env.NODE_ENV === 'development') {
      infoLog(`[landing] ${label}`, { t: Date.now(), ...data })
    }
  }

  const landingRequest = useMemo<ILandingDetailsRequest | null>(
    () =>
      location != null
        ? {
            latitude: location.latitude,
            longitude: location.longitude,
            radius,
          }
        : null,
    [location?.latitude, location?.longitude, radius],
  )
  useLandingPoll(landingRequest, landingDetail)

  useEffect(() => {
    devLandingLog('home_mount')
  }, [])


  useEffect(() => {
    const { fullName, eaterId, email } = generateEater(user ?? {})

    if(analytics && isAnalyticsReady){
      analytics?.page('PAGE_OPENED', {
        path: ROUTES.HOME,
        title: DocumentTitle,
        url: window.location.origin,
        category: 'Home',
        name: 'Home Page Viewed',
        eaterName: fullName,
        startTime: new Date().toISOString(),
        email,
        eaterId,
      })
    }

    if (isAuthenticated) {
      analytics?.identify(eaterId, {
        name: fullName,
        email,
      })
    }
  }, [analytics, isAuthenticated, user, isAnalyticsReady])

  useEffect(() => {
    const { fullName, eaterId, email } = generateEater(user ?? {})

    return () => {
      analytics?.page('PAGE_CLOSED', {
        path: ROUTES.HOME,
        title: DocumentTitle,
        url: window.location.origin,
        category: 'Home',
        name: 'Home Page Viewed',
        eaterName: fullName,
        endTime: new Date().toISOString(),
        email,
        eaterId
      })
    }

    }, [navLocation, analytics]);

  // Fire first landing request as soon as we have location + radius. Do not wait on auth.
  // Single location path: use Redux location if present; otherwise fetch once and store.
  useEffect(() => {
    if (firstLandingRequestFiredRef.current) return
    firstLandingRequestFiredRef.current = true

    const fireLanding = async (lat: number, lng: number): Promise<void> => {
      const request: ILandingDetailsRequest = { latitude: lat, longitude: lng, radius }
      if (!accessToken) firstLandingWasUnauthRef.current = true
      devLandingLog('landing_request_start', { hasAuth: !!accessToken })
      await Sentry.startSpan({ name: 'home:fetchLanding' }, async () => {
        await dispatch(getLandingPageDetails(request))
      })
    }

    // Fast, NON-BLOCKING location for the first paint. Precise browser geolocation
    // (getLocation) can block 10-15s and used to gate the landing call entirely — that was the
    // slow/flaky landing. Order: Redux -> cached localStorage -> IP lookup (fast, no permission
    // prompt, country/city-accurate for international) -> hardcoded default.
    const resolveFast = async (): Promise<{ latitude: number; longitude: number }> => {
      if (location?.latitude != null && location?.longitude != null) {
        return { latitude: location.latitude, longitude: location.longitude }
      }
      try {
        const cached = window.localStorage.getItem('location')
        if (cached) {
          const c = JSON.parse(cached)
          if (c?.latitude != null && c?.longitude != null) {
            return { latitude: c.latitude, longitude: c.longitude }
          }
        }
      } catch {
        /* ignore corrupt cache */
      }
      const ip = await getGoogleMapsLocation()
      return ip ?? defaultLocationCoords
    }

    const run = async (): Promise<void> => {
      // 1) Fire landing immediately with the fast location — never waits on GPS.
      const fast = await resolveFast()
      await fireLanding(fast.latitude, fast.longitude)

      // 2) Refine in the background with precise geolocation; only refetch if it actually moved.
      //    Non-blocking; failures are swallowed since landing has already loaded.
      void Sentry.startSpan({ name: 'home:getLocation' }, async () => {
        try {
          const precise = await getLocation()
          if (
            precise?.latitude != null &&
            precise?.longitude != null &&
            (precise.latitude !== fast.latitude || precise.longitude !== fast.longitude)
          ) {
            dispatch(setLocation(precise))
            try {
              window.localStorage.setItem('location', JSON.stringify(precise))
            } catch {
              /* localStorage unavailable — cache just won't warm */
            }
            await fireLanding(precise.latitude, precise.longitude)
          }
        } catch (e) {
          errorLog('[Home] precise getLocation failed (non-blocking)', e)
        }
      })
    }

    const id = requestAnimationFrame(() => {
      void run()
    })
    return () => cancelAnimationFrame(id)
  }, [radius, location?.latitude, location?.longitude, dispatch])

  useEffect(() => {
    if (landingDetail != null && firstLandingRequestFiredRef.current) {
      if (landingAuthRefreshInFlightRef.current) {
        devLandingLog('landing_auth_refresh_response')
        landingAuthRefreshInFlightRef.current = false
      } else {
        devLandingLog('landing_response', {
          restaurantCount: landingDetail.restaurantCount,
        })
      }
    }
  }, [landingDetail])

  useEffect(() => {
    if (accessToken) devLandingLog('auth_token_ready')
  }, [accessToken])

  // Optional: one background refresh with auth if token appears after unauth landing
  useEffect(() => {
    if (
      !firstLandingWasUnauthRef.current ||
      !accessToken ||
      !landingRequest ||
      landingAuthRefreshDoneRef.current ||
      landingAuthRefreshInFlightRef.current
    ) return
    if (landingDetail == null) return
    landingAuthRefreshDoneRef.current = true
    landingAuthRefreshInFlightRef.current = true
    devLandingLog('landing_auth_refresh_start')
    void dispatch(getLandingPageDetails(landingRequest))
  }, [accessToken, landingRequest, landingDetail, dispatch])

  // Track home_loaded once per tab session, when data is first ready. sessionStorage guard
  // (mirrors App.tsx app_opened) so it survives StrictMode double-invoke, HMR, and remounts —
  // deps still mutate as lists enrich/paginate, but the session key prevents re-firing.
  useEffect(() => {
    if (!loading && (walletList ?? localList)) {
      try {
        if (sessionStorage.getItem('rsly_home_loaded') === '1') return
        sessionStorage.setItem('rsly_home_loaded', '1')
      } catch {
        // sessionStorage unavailable — fall back to the per-mount ref guard
        if (homeLoadedLoggedRef.current) return
        homeLoadedLoggedRef.current = true
      }
      logEvent('home_loaded', {
        screen_name: 'home',
        source: 'in_app',
      })
    }
  }, [loading, walletList, localList])

  // Enrichment effect: fetch predictions after base data loads (only once)
  useEffect(() => {
    const enrichLandingLists = async (): Promise<void> => {
      // Only enrich if:
      // 1. Base data has loaded (not loading)
      // 2. User is authenticated
      // 3. We have at least one list
      // 4. We haven't already enriched
      if (
        !loading &&
        isAuthenticated &&
        !hasEnrichedRef.current &&
        (walletList ?? localList)
      ) {
        hasEnrichedRef.current = true
        const local = location ?? await getLocation()
        const request: ILandingDetailsRequest = {
          longitude: local?.longitude,
          latitude: local?.latitude,
          radius,
        }

        // Dispatch enrichment thunks for active lists only
        void Promise.all([
          dispatch(
            getLandingListWithPredictions({
              type: LandingType.WALLET,
              page: 0,
              pageSize: 10,
              request,
            }),
          ),
          dispatch(
            getLandingListWithPredictions({
              type: LandingType.LOCAL,
              page: 0,
              pageSize: 10,
              request,
            }),
          ),
        ])
      }
    }

    void enrichLandingLists()
  }, [
    loading,
    isAuthenticated,
    walletList,
    localList,
    location,
    radius,
    dispatch,
  ])

  // Reset enrichment and first-landing flags when radius changes (re-fetch landing)
  useEffect(() => {
    hasEnrichedRef.current = false
    firstLandingRequestFiredRef.current = false
  }, [radius])

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  // eslint-disable-next-line
  const fetchData = async (): Promise<void> => {
    await Sentry.startSpan({ name: "home:fetchLanding" }, async () => {
      const local = location ?? await getLocation();
      const request: ILandingDetailsRequest = { longitude: local?.longitude, latitude: local?.latitude, radius };
      await dispatch(getLandingPageDetails(request));
    });
  };

  return (
    <div>
      <Helmet>
        <title>{DocumentTitle}</title>
        <meta
          name="description"
          content="Discover restaurants you'll actually love — recommendations built from what diners really do, not five-star noise. Find the right table before you get there, and see what to order once you arrive."
        />
      </Helmet>

      {/* Rewards & Profile Banners */}
      <Container component="div" sx={{ flex: 1 }}>
        <RewardsCTABanner />
        <GroupDiningCTA />
        <ProfileCompletionBanner />
      </Container>

      {/* Menu Scan Banner */}
      <Container component="div">
        <MenuScanBanner
          entryPoint="home_banner"
          onFileSelected={(file, uploadMethod) => {
            void startScan(file, undefined, uploadMethod).catch(() => {})
          }}
          disabled={uploading}
          headline="See what to order"
          subtext="Use a menu photo to see what to order and earn points."
          mobileSubtext="Use the camera below to see what to order and earn points."
          buttonText="Try it"
          hideButtonOnMobile={true}
        />
      </Container>

      {/* PRIMARY SECTION: Where Should You Go? */}
      <FoodAreasSection loading={loading} />

      {/* Wallet Friendly & Local Flavors Sections */}
      <Container component="div">
        <HomeRestaurantCardContainer>
          <RestaurantCardContainer
            title={localList?.name ?? 'Local Flavors To Explore'}
            cardList={localList}
            loading={loading}
            name={LandingType.LOCAL}
            isHybrid={isHybrid}
            recommendationId={landingDetail?.recommendationId}
            modelVersion={landingDetail?.modelVersion}
          />
        </HomeRestaurantCardContainer>

        <HomeRestaurantCardContainer mb={isHybrid ? '10px' : '0px'}>
          <RestaurantCardContainer
            title={walletList?.name ?? 'Wallet Friendly Restaurants'}
            cardList={walletList}
            loading={loading}
            name={LandingType.WALLET}
            isHybrid={isHybrid}
            recommendationId={landingDetail?.recommendationId}
            modelVersion={landingDetail?.modelVersion}
          />
        </HomeRestaurantCardContainer>

        {/* Social Groups Section */}
        {isAuthenticated && <GroupsConversationsSection />}
      </Container>
      {!isHybrid && <DownloadApp />}
      <MuiSnackbar open={openServer} onClose={setOpenServer}>
        <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </div>
  )
}

export default Home
